import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

interface PasswordEntity {
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-reset-password-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reset-password-modal.html',
  styleUrls: ['./reset-password-modal.scss'],
})
export class ResetPasswordModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() userId: string = '';
  @Input() errorMessage: string = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() passwordReset = new EventEmitter<{ userId: string; newPassword: string }>();

  @ViewChild('ResetPasswordForm') resetPasswordForm?: NgForm;

  entity: PasswordEntity = {
    newPassword: '',
    confirmPassword: '',
  };

  isLoading: boolean = false;
  pswType: string = 'password';
  confirmPswType: string = 'password';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['errorMessage'] && this.errorMessage === '') {
      this.isLoading = false;
    }

    if (changes['visible'] && !this.visible && changes['visible'].previousValue === true) {
      if (!this.errorMessage) {
        this.resetForm();
      }
    }
  }

  hasMinLength(): boolean {
    return this.entity.newPassword ? this.entity.newPassword.length >= 8 : false;
  }

  hasUpperCase(): boolean {
    return this.entity.newPassword ? /[A-Z]/.test(this.entity.newPassword) : false;
  }

  hasLowerCase(): boolean {
    return this.entity.newPassword ? /[a-z]/.test(this.entity.newPassword) : false;
  }

  hasNumber(): boolean {
    return this.entity.newPassword ? /\d/.test(this.entity.newPassword) : false;
  }

  hasSpecialChar(): boolean {
    return this.entity.newPassword ? /[@$!%*?&#]/.test(this.entity.newPassword) : false;
  }

  passwordMismatch(): boolean {
    return (
      this.entity.newPassword !== this.entity.confirmPassword &&
      this.entity.confirmPassword.length > 0
    );
  }

  canSave(): boolean {
    if (!this.resetPasswordForm) return false;

    return (
      this.resetPasswordForm.valid &&
      !this.passwordMismatch() &&
      this.hasMinLength() &&
      this.hasUpperCase() &&
      this.hasLowerCase() &&
      this.hasNumber() &&
      this.hasSpecialChar()
    );
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
    if (!visible) {
      this.resetForm();
    }
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  resetForm(): void {
    this.entity = {
      newPassword: '',
      confirmPassword: '',
    };
    this.isLoading = false;
    this.pswType = 'password';
    this.confirmPswType = 'password';

    if (this.resetPasswordForm) {
      this.resetPasswordForm.resetForm();
    }
  }

  changetype(): void {
    this.pswType = this.pswType === 'password' ? 'text' : 'password';
  }

  changeConfirmType(): void {
    this.confirmPswType = this.confirmPswType === 'password' ? 'text' : 'password';
  }

  generatePassword(): void {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '@$!%*?&#';
    const allChars = lowercase + uppercase + numbers + special;

    let password = '';

    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += special.charAt(Math.floor(Math.random() * special.length));

    for (let i = 4; i < 12; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    password = password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');

    this.entity.newPassword = password;
    this.entity.confirmPassword = password;
  }

  onSave(): void {
    if (this.canSave()) {
      this.isLoading = true;
      this.passwordReset.emit({
        userId: this.userId,
        newPassword: this.entity.newPassword,
      });
    }
  }
}
